// packages/clients/Center-PWA/src/reducers/notificationsReducer.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ApiClient } from '@center-technologies/shared-api-clients';

export interface NotificationRecord {
  id: string;
  user_id: string;
  sphere_id?: string;
  center_id?: string;
  type: string;
  title: string;
  content: string;
  summary?: string;
  metadata?: Record<string, unknown>;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  read_at?: string;
}

interface NotificationsState {
  /** All cached notifications keyed by ID */
  byId: Record<string, NotificationRecord>;
  /** IDs of all notifications in order (newest first) */
  allIds: string[];
  /** Timestamp of the most recent notification (for incremental loading) */
  lastFetchedDate: string | null;
  /** Sphere IDs we've fetched notifications for */
  fetchedSphereIds: string[];
  /** Loading state */
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  /** Error message */
  error: string | null;
  /** Unread count */
  unreadCount: number;
}

const initialState: NotificationsState = {
  byId: {},
  allIds: [],
  lastFetchedDate: null,
  fetchedSphereIds: [],
  status: 'idle',
  error: null,
  unreadCount: 0,
};

interface FetchNotificationsArgs {
  apiClient: ApiClient;
  userId: string;
  sphereIds: string[];
  /** If true, fetches all notifications. If false, only fetches new ones since lastFetchedDate */
  fullRefresh?: boolean;
}

interface FetchNotificationsResponse {
  notifications: NotificationRecord[];
  latestDate: string | null;
  sphereIds: string[];
}

/**
 * Fetch notifications for multiple spheres with incremental loading support
 */
export const fetchNotifications = createAsyncThunk<
  FetchNotificationsResponse,
  FetchNotificationsArgs,
  { rejectValue: string; state: { notifications: NotificationsState } }
>(
  'notifications/fetchNotifications',
  async ({ apiClient, userId, sphereIds, fullRefresh = false }, thunkAPI) => {
    try {
      const state = thunkAPI.getState().notifications;

      // Build query params
      const params = new URLSearchParams();
      if (sphereIds.length > 0) {
        params.set('sphereIds', sphereIds.join(','));
      }

      // For incremental loading, use lastFetchedDate unless fullRefresh
      if (!fullRefresh && state.lastFetchedDate) {
        params.set('since', state.lastFetchedDate);
      }

      params.set('limit', '100');

      const queryString = params.toString();
      const querySuffix = queryString ? `?${queryString}` : '';
      const url = `/notifications/${userId}${querySuffix}`;

      const response = await apiClient.get<{
        success: boolean;
        data: NotificationRecord[];
        latestDate: string | null;
        error?: string;
      }>(url);

      if (!response.success) {
        return thunkAPI.rejectWithValue(response.error || 'Failed to fetch notifications');
      }

      return {
        notifications: response.data,
        latestDate: response.latestDate,
        sphereIds,
      };
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      return thunkAPI.rejectWithValue(err.message ?? 'Unknown error');
    }
  }
);

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = createAsyncThunk<
  { notificationId: string },
  { apiClient: ApiClient; notificationId: string; userId: string },
  { rejectValue: string }
>(
  'notifications/markAsRead',
  async ({ apiClient, notificationId, userId }, thunkAPI) => {
    try {
      await apiClient.patch(`/notifications/${notificationId}/read`, { userId });
      return { notificationId };
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      return thunkAPI.rejectWithValue(err.message ?? 'Unknown error');
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /** Clear all cached notifications (e.g., on logout or context change) */
    clearNotifications: (state) => {
      state.byId = {};
      state.allIds = [];
      state.lastFetchedDate = null;
      state.fetchedSphereIds = [];
      state.unreadCount = 0;
      state.error = null;
    },
    /** Add sphere IDs to the tracked list */
    addTrackedSphereIds: (state, action: PayloadAction<string[]>) => {
      const newIds = action.payload.filter(id => !state.fetchedSphereIds.includes(id));
      state.fetchedSphereIds = [...state.fetchedSphereIds, ...newIds];
    },
    /** Optimistically mark notification as read in cache */
    markAsReadLocal: (state, action: PayloadAction<string>) => {
      const notification = state.byId[action.payload];
      if (notification && notification.status !== 'read') {
        notification.status = 'read';
        notification.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchNotifications
      .addCase(fetchNotifications.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { notifications, latestDate, sphereIds } = action.payload;

        // Merge new notifications with existing ones
        for (const notification of notifications) {
          if (!state.byId[notification.id]) {
            state.allIds.push(notification.id);
          }
          state.byId[notification.id] = notification;
        }

        // Sort allIds by created_at (newest first)
        state.allIds.sort((a, b) => {
          const dateA = new Date(state.byId[a]?.created_at || 0);
          const dateB = new Date(state.byId[b]?.created_at || 0);
          return dateB.getTime() - dateA.getTime();
        });

        // Update lastFetchedDate
        if (latestDate && (!state.lastFetchedDate || latestDate > state.lastFetchedDate)) {
          state.lastFetchedDate = latestDate;
        }

        // Track which spheres we've fetched
        for (const sphereId of sphereIds) {
          if (!state.fetchedSphereIds.includes(sphereId)) {
            state.fetchedSphereIds.push(sphereId);
          }
        }

        // Recalculate unread count
        state.unreadCount = Object.values(state.byId).filter(
          n => n.status !== 'read'
        ).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? null;
      })

      // markNotificationAsRead
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.byId[action.payload.notificationId];
        if (notification && notification.status !== 'read') {
          notification.status = 'read';
          notification.read_at = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });
  },
});

export const { clearNotifications, addTrackedSphereIds, markAsReadLocal } = notificationsSlice.actions;

// Selectors
export const selectAllNotifications = (state: { notifications: NotificationsState }) =>
  state.notifications.allIds.map(id => state.notifications.byId[id]).filter(Boolean);

export const selectNotificationsBySphereIds = (
  state: { notifications: NotificationsState },
  sphereIds: string[]
) =>
  state.notifications.allIds
    .map(id => state.notifications.byId[id])
    .filter(n => n && sphereIds.includes(n.sphere_id || ''));

export const selectUnreadCount = (state: { notifications: NotificationsState }) =>
  state.notifications.unreadCount;

export const selectNotificationsStatus = (state: { notifications: NotificationsState }) =>
  state.notifications.status;

export const selectNotificationsError = (state: { notifications: NotificationsState }) =>
  state.notifications.error;

export const selectLastFetchedDate = (state: { notifications: NotificationsState }) =>
  state.notifications.lastFetchedDate;

export const selectFetchedSphereIds = (state: { notifications: NotificationsState }) =>
  state.notifications.fetchedSphereIds;

export default notificationsSlice.reducer;
